import React, { ChangeEvent, FC, useCallback, useState } from "react";
import { Button, Card, IconButton, Stack, Typography } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import { ProcurementSupplierDto } from "@green0meter/api-client";
import { debounce, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";


import Table from "./components/Table";
import SupplierEditor from "./components/SupplierEditor";
import ImportCsv from "./components/ImportCsv";
import { handleDownloadTemplateFile } from "./Suppliers.helpers";

import { TextField } from "@components/base";
import { useUserStore } from "@store/user";
import {
  useCreateProcurementSupplier,
  useExportProcurementSuppliers,
  useGetProcurementSuppliers,
  useImportProcurementSuppliers,
  useUpdateProcurementSupplier,
} from "@api";


interface Component {}

const Component: FC<Component> = () => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [editorOpen, setEditorOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [orderBy, setOrderBy] = useState<string>();
  const [orderDirection, setOrderDirection] = useState<"ASC" | "DESC">("ASC");

  const [editProcurementSupplier, setEditProcurementSupplier] =
    useState<ProcurementSupplierDto>();
  const { procurementSuppliers, procurementSuppliersEnabled } =
    useGetProcurementSuppliers(user);
  const { updateProcurementSupplierEnabled } = useUpdateProcurementSupplier(user);
  const { importProcurementSuppliersEnabled } = useImportProcurementSuppliers(user);
  const { createProcurementSupplierEnabled } = useCreateProcurementSupplier(user);

  // TODO: implement notification with loading
  const { exportProcurementSuppliers, exportProcurementSuppliersEnabled } =
    useExportProcurementSuppliers(user);

  const handleOpenEditor = (): void => {
    setEditorOpen(true);
  };

  const handleCloseEditor = (): void => {
    setEditorOpen(false);
  };

  const handleCloseImport = (): void => {
    setImportOpen(false);
  };

  const handleOpenImport = (): void => {
    setImportOpen(true);
  };
  const handleExport = (): void => {
    exportProcurementSuppliers();
  };

  const handleCreateSupplier = (): void => {
    setEditProcurementSupplier(undefined);
    handleOpenEditor();
  };

  const handleEditSupplier = (row: ProcurementSupplierDto): void => {
    if (updateProcurementSupplierEnabled) {
      setEditProcurementSupplier(row);
      setEditorOpen(true);
    }
  };

  const [searchQuery, setSearchQuery] = useState("");

  const debounceSearchQuery = useCallback(debounce(setSearchQuery, 1000), []);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    const searchValue = event.target.value.toLowerCase();

    debounceSearchQuery(searchValue);
  };

  return (
    procurementSuppliersEnabled && (
      <>
        <Card>
          <Stack p={3} spacing={3}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">{t("sellers.listTitle")}</Typography>
                <IconButton size="small" color="primary">
                  <FeatherIcon icon="info" size={16} />
                </IconButton>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  placeholder={t("common.search")}
                  size="small"
                  variant="filled"
                  slotProps={{
                    input: { startAdornment: <FeatherIcon icon="search" /> },
                  }}
                  onChange={handleSearch}
                />
                {createProcurementSupplierEnabled && (
                  <Button
                    variant="text"
                    startIcon={<FeatherIcon icon="plus" size={16} />}
                    size="small"
                    onClick={handleCreateSupplier}
                  >
                    {t("sellers.add")}
                  </Button>
                )}
                {importProcurementSuppliersEnabled && (
                  <Button
                    variant="text"
                    startIcon={<FeatherIcon icon="upload" size={16} />}
                    size="small"
                    onClick={handleOpenImport}
                  >
                    {t("sellers.import")}
                  </Button>
                )}
                {exportProcurementSuppliersEnabled && (
                  <Button
                    variant="text"
                    startIcon={<FeatherIcon icon="download" size={16} />}
                    size="small"
                    onClick={handleExport}
                  >
                    {t("sellers.export")}
                  </Button>
                )}
              </Stack>
            </Stack>
            <Table
              onRowClick={handleEditSupplier}
              search={searchQuery}
              orderBy={orderBy}
              orderDirection={orderDirection}
              setOrderBy={setOrderBy}
              setOrderDirection={setOrderDirection}
            />
            {isEmpty(procurementSuppliers?.items) && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Button
                  variant="outlined"
                  startIcon={<FeatherIcon icon="download" size={16} />}
                  onClick={handleDownloadTemplateFile}
                >
                  {t("sellers.downloadTemplate")}
                </Button>
                {importProcurementSuppliersEnabled && (
                  <Button
                    variant="contained"
                    startIcon={<FeatherIcon icon="upload" size={16} />}
                    onClick={handleOpenImport}
                  >
                    {t("sellers.importList")}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Card>
        <SupplierEditor
          open={editorOpen}
          onClose={handleCloseEditor}
          procurementSupplier={editProcurementSupplier}
        />
        <ImportCsv open={importOpen} onClose={handleCloseImport} />
      </>
    )
  );
};

export default Component;
